// =====================================================
// i18n format helpers — Phase 12.
//
// S-UI-JALALI: the calendar arithmetic that used to live here (an inlined
// Gregorian→Jalali routine plus an ICU probe) has moved to `./jalali`, which
// is the single canonical source and is anchored to Asia/Tehran rather than
// to whatever timezone the viewer's browser happens to be in. This module
// keeps its own signatures — `withTime`, `monthAsName`, «—» for an empty
// value — so its 25 call sites stay untouched and simply inherit the fix.
//
// S-I18N-W1a: and the same trick again, one layer up. Every function here is
// locale-aware now (Jalali + U+06F0 digits for `fa`, Gregorian + U+0660 for
// `ar`, per ADR-A4) while every signature stays byte-identical, so the 144
// files that call these get Arabic support without being touched.
// =====================================================

import {
  formatJalali,
  formatJalaliLong,
  formatJalaliTime,
  toFaDigits as toFaDigitsCore,
} from "./jalali";
import { formatLocaleNumber, formatLocaleRelativeTime } from "./intl";
import { getLocale, translate } from "./locale-state";

export const toFaDigits = toFaDigitsCore;

/**
 * S-I18N-W5-ADMIN — یک «رشتهٔ شناسه» که عدد نیست: شمارهٔ دانشجویی، کد پرسنلی، سالِ
 * شمسی. ارقام باید localeِ خواننده را دنبال کنند ولی جداکنندهٔ هزارگان نباید بیاید
 * (`۱۴۰۴` است، نه `۱٬۴۰۴`) — پس `formatNumberFa` این‌جا جواب نمی‌دهد.
 */
export const formatDigits = (value) => toFaDigits(String(value ?? ""));

/**
 * S-STUDIO-P1-W3B — a ready-made numeric string (a subtitle timecode, a counter) whose
 * DIGITS must follow the reading locale: «۰۱:۰۲٫۵» in fa, «٠١:٠٢٫٥» in ar.
 *
 * Why it exists instead of calling `toFaDigits` at the call site: the name lies. `toFaDigits`
 * is locale-aware — it has been since S-I18N-W1a — but a reader of a component sees "Fa" and
 * reasonably assumes Persian is hardcoded, which is exactly the kind of quiet bilingual bug
 * that only shows up on an Arabic screen nobody opened. Screens ask for `formatTimecode` and
 * the locale question stays in `i18n/`, where it is answered once.
 *
 * The value is NOT parsed or re-formatted: a timecode is a clock, not a number, and
 * `toLocaleString` would happily turn "01:02.500" into something else entirely.
 */
export const formatTimecode = (clock) => formatDigits(clock);

const coerceDate = (d) => {
  if (!d) return null;
  if (d instanceof Date) return isNaN(d.getTime()) ? null : d;
  const parsed = new Date(d);
  return isNaN(parsed.getTime()) ? null : parsed;
};

export const formatJalaliDate = (d, { withTime = false, monthAsName = true } = {}) => {
  const date = coerceDate(d);
  if (!date) return "—";
  const datePart = monthAsName ? formatJalaliLong(date) : formatJalali(date);
  return withTime ? `${datePart} — ${formatJalaliTime(date)}` : datePart;
};

/**
 * Group separator, decimal mark and percent sign per STYLE_AR §٣.
 *
 * `fa` is deliberately left on the ASCII "," / "." it has always printed —
 * this package does not restyle Persian numbers, it only adds Arabic. The
 * Arabic marks (U+066C thousands, U+066B decimal) are mandatory there.
 */
const AR_GROUP = "٬";
const AR_DECIMAL = "٫";
/** Arabic percent sign, U+066A. */
export const PERCENT_SIGN_AR = "٪";

/**
 * Registry-backed number formatter for an explicit platform locale.
 *
 * Persian keeps its historical ASCII comma/decimal marks for compatibility;
 * every other locale receives the numbering system and punctuation declared
 * in the 50-locale registry through `formatLocaleNumber`.
 */
export const formatNumberForLocale = (n, locale, { unit = "" } = {}) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  const localized = formatLocaleNumber(Number(n), locale);
  const str = locale === "fa"
    ? localized
      .replace(/[\u061c\u200e\u200f]/g, "")
      .replaceAll("−", "-")
      .replaceAll(AR_GROUP, ",")
      .replaceAll(AR_DECIMAL, ".")
    : localized;
  return unit ? `${str} ${unit}` : str;
};

/** Registry-backed percent formatter; fa/ar retain the established leading sign. */
export const formatPercentForLocale = (n, locale) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  if (locale === "fa" || locale === "ar") {
    return `${PERCENT_SIGN_AR}${formatNumberForLocale(Number(n), locale)}`;
  }
  return formatLocaleNumber(Number(n) / 100, locale, { style: "percent" });
};

/** Backward-compatible active-locale entry point used throughout the product. */
export const formatNumberFa = (n, options) => formatNumberForLocale(n, getLocale(), options);

/** Backward-compatible active-locale percent entry point. */
export const formatPercent = (n) => formatPercentForLocale(n, getLocale());

/**
 * "۵ دقیقه پیش" / "قبل ٥ دقائق".
 *
 * S-I18N-W1a: the four hard-coded Persian branches are gone — the wording now
 * comes from `Intl.RelativeTimeFormat`, which knows Arabic's dual and its
 * plural breaks. `numeric: "always"` keeps the Persian output identical to
 * what this function printed before (the "auto" mode would silently turn
 * «۲ روز پیش» into «پریروز»). Only the sub-minute case stays a real string,
 * because no `Intl` API produces «همین الان».
 */
export const formatRelativeFa = (d) => {
  const date = coerceDate(d);
  if (!date) return "—";
  const locale = getLocale();
  const diffMin = Math.round((Date.now() - date.getTime()) / 60000);
  if (diffMin < 1) return translate("common:time.justNow", "همین الان");

  let value;
  let unit;
  if (diffMin < 60) {
    value = diffMin;
    unit = "minute";
  } else {
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) {
      value = diffHr;
      unit = "hour";
    } else {
      const diffDay = Math.round(diffHr / 24);
      if (diffDay >= 7) return formatJalaliDate(date, { monthAsName: true });
      value = diffDay;
      unit = "day";
    }
  }

  try {
    return formatLocaleRelativeTime(-value, unit, locale, { numeric: "always" });
  } catch {
    // Runtime without RelativeTimeFormat — fall back to the absolute date
    // rather than to a hard-coded sentence in one language.
    return formatJalaliDate(date, { monthAsName: true });
  }
};

export default formatJalaliDate;
